import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

export default function GallerySection({ content }) {
  const [active, setActive] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const items = content.gallery || [];

  const close = useCallback(() => {
    setActive(null);
    setZoomed(false);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (active === null) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") setActive((a) => (a + 1) % items.length);
      if (e.key === "ArrowLeft") setActive((a) => (a - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, items.length, close]);

  return (
    <section id="galeri" className="bg-fade-blue py-24" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeading
          number="08"
          eyebrow="Galeri"
          title="Galeri Perumahan Pontianak"
          description="Foto tampak depan, interior, dan lingkungan perumahan. Klik untuk memperbesar."
        />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {items.map((g, i) => (
            <Reveal key={i} delay={(i % 4) * 0.06} className={i % 5 === 0 ? "md:col-span-2 md:row-span-2" : ""}>
              <button
                onClick={() => setActive(i)}
                className="group relative block w-full overflow-hidden rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand"
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.url}
                  alt={`${g.caption} — galeri perumahan PT. Cahaya Sakofa Mulia`}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i % 5 === 0 ? "aspect-square md:aspect-auto md:h-full" : "aspect-square"}`}
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-dark/70 to-transparent p-4 text-left opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <span className="block text-sm font-semibold text-white">{g.caption}</span>
                  <span className="text-xs text-white/70">{g.category}</span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-brand-dark/90 p-4 backdrop-blur-sm"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button onClick={close} aria-label="Tutup galeri" data-testid="lightbox-close" className="absolute right-5 top-5 rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20">
              <X className="h-6 w-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setActive((active - 1 + items.length) % items.length); setZoomed(false); }}
              aria-label="Foto sebelumnya"
              data-testid="lightbox-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20 sm:left-6"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setActive((active + 1) % items.length); setZoomed(false); }}
              aria-label="Foto berikutnya"
              data-testid="lightbox-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20 sm:right-6"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-h-[85vh] max-w-5xl overflow-auto rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[active].url}
                alt={items[active].caption}
                onClick={() => setZoomed((z) => !z)}
                className={`max-h-[80vh] w-auto cursor-zoom-in rounded-2xl object-contain transition-transform duration-300 ${zoomed ? "scale-150 cursor-zoom-out" : ""}`}
                data-testid="lightbox-image"
              />
              <p className="mt-3 text-center text-sm font-medium text-white/80">{items[active].caption}</p>
            </motion.div>
            <button
              onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
              aria-label="Zoom foto"
              data-testid="lightbox-zoom"
              className="absolute bottom-5 right-5 rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20"
            >
              {zoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
